import {
    AppIcon,
    AppText,
    Box,
    CallLogWarningMessage,
    Column,
    IconAvatarCell,
    IconNames,
    Row,
    TableColumn,
    TableDataRecord,
    TeamMember,
    Theme,
    Tooltip,
} from '@streem/ui-react';
import { APITypes, StreemApiCallLogEntryCallStatus } from '@streem/api';
import { formatDateColumn } from '@streem/toolbox';
import {
    callHasEnded,
    getIconAvatarDetails,
    getToFromUsers,
    getCallLogRowErrorMessage,
    getCallLogWarnings,
} from '../../util/calls';
import { recordTooltipViewed } from '@streem/analytics';
import { getFormattedCallDuration } from '../../util/call_duration_helpers';
import { DropdownMenuColumn } from './dropdown_menu_column';
import { FC, useState } from 'react';

export type CallLogRow = APITypes.StreemApiCallLogEntry & TableDataRecord;

export interface CallLogColumnProps {
    theme: typeof Theme;
    hideGPSLocations?: boolean;
    callOutcomeEnabled?: boolean;
    handleRoomArchive?: (row: CallLogRow) => void;
    showDeactivateColumn?: boolean;
    sendNoteEnabled?: boolean;
}

const SUCCESSFUL_CALL_STATUS = 'STATUS_SUCCESSFUL';

const CustomerColumn: FC<React.PropsWithChildren<{ row: CallLogRow; theme: typeof Theme }>> = ({
    row,
    theme,
}) => {
    const { customer } = getToFromUsers(row);
    const { title, icon, color } = getIconAvatarDetails(row, customer, theme);
    return (
        <IconAvatarCell
            headline={customer.name ?? 'No Customer Found'}
            subHeadline={row.referenceId ?? 'No reference ID available'}
            icon={icon}
            fillColor={color}
            backgroundColor={''}
            title={title}
            maskHeadline
        />
    );
};

function customerColumn(props: CallLogColumnProps): TableColumn<CallLogRow> {
    return {
        key: 'customer',
        name: 'Customer',
        sortable: false,
        render: callLogRow => {
            const { callOutcomeEnabled, sendNoteEnabled, theme } = props;
            const { showDispositionNotSubmitted, showNoteNotSent } = getCallLogWarnings({
                callLogRow,
                callOutcomeEnabled,
                sendNoteEnabled,
            });
            const dataTestId = `my-call-log-row-${callLogRow.roomSid}`;

            const errorMessage = getCallLogRowErrorMessage(
                showDispositionNotSubmitted,
                showNoteNotSent,
            );

            return (
                <Row alignItems="center" data-testid={dataTestId} flexGrow={1}>
                    <Row flexBasis="300px" mr={4}>
                        <CustomerColumn row={callLogRow} theme={theme} />
                    </Row>
                    {errorMessage && <CallLogWarningMessage message={errorMessage} />}
                </Row>
            );
        },
    };
}

interface TeamMemberCellProps {
    row: CallLogRow;
}

function TeamMemberCell({ row }: TeamMemberCellProps) {
    const { expert: teamMember } = getToFromUsers(row);

    return <TeamMember name={teamMember.name} avatarUrl={teamMember.avatarUrl} />;
}

function teamMemberColumn(): TableColumn<CallLogRow> {
    return {
        key: 'teamMember',
        name: 'Team Member',
        sortable: false,
        render: row => <TeamMemberCell row={row} />,
    };
}

function geoLocationColumn(props: CallLogColumnProps): TableColumn<CallLogRow> {
    return {
        key: 'geolocation',
        name: 'Last Location',
        sortable: false,
        render: row => {
            if (!row?.latestGpsPosition) {
                return (
                    <Box display="grid" gridTemplateColumns="1fr" alignItems="center">
                        <AppText color="medium">No location available</AppText>
                    </Box>
                );
            }

            return (
                <Box display="grid" gridTemplateColumns="40px 1fr" alignItems="center">
                    <AppIcon name="GPSIcon" size="large" color={props.theme.colors.grey90} />
                    <AppText>{row.latestGpsPosition.formattedAddress}</AppText>
                </Box>
            );
        },
    };
}

export function artifactColumn(props: CallLogColumnProps): TableColumn<CallLogRow> {
    return {
        key: 'artifact',
        name: 'Call Details',
        contentJustify: 'center',
        sortable: false,
        render: row => {
            if (
                row.callStatus !== SUCCESSFUL_CALL_STATUS ||
                (row.artifactCounts.streemshot === 0 &&
                    row.artifactCounts.gpsPosition === 0 &&
                    row.artifactCounts.bookmark === 0 &&
                    !row.requeued)
            ) {
                return null;
            }
            return (
                <Row justifyContent="center" width={'100%'}>
                    {row.requeued && (
                        <CallLogIcon
                            icon="CallTransferIcon"
                            data-testid="transfer-icon"
                            iconSize="24"
                        />
                    )}
                    {row.artifactCounts.streemshot > 0 && (
                        <CallLogIcon
                            icon="StreemshotIcon"
                            data-testid="streemshot-icon"
                            count={row.artifactCounts.streemshot}
                            countStyles={{ marginLeft: '4px' }}
                        />
                    )}
                    {row.artifactCounts.gpsPosition > 0 && !props.hideGPSLocations && (
                        <CallLogIcon
                            data-testid="gps-icon"
                            icon="GPSIcon"
                            count={row.artifactCounts.gpsPosition}
                        />
                    )}
                    {row.artifactCounts.bookmark > 0 && (
                        <CallLogIcon
                            data-testid="bookmark-icon"
                            icon="BookmarkIcon"
                            count={row.artifactCounts.bookmark}
                        />
                    )}
                </Row>
            );
        },
    };
}

const CallLogIcon: FC<
    React.PropsWithChildren<{
        count?: number;
        countStyles?: React.CSSProperties;
        icon: IconNames;
        iconSize?: string;
        shouldMask?: boolean;
        'data-testid'?: string;
    }>
> = ({ count, countStyles, icon, iconSize = '18', shouldMask = false, 'data-testid': testId }) => {
    const testIdentifier = testId || `call-log-${icon}`;
    return (
        <Row
            mr={3}
            maxWidth={'58px'}
            borderRadius={8}
            backgroundColor={'#f4f7f9'}
            padding="6px"
            alignItems="center"
            justifyContent="space-between"
            data-testid={testIdentifier}
        >
            <AppIcon iconSize={iconSize} name={icon} />
            {count != null && (
                <AppText
                    data-testid={`${testIdentifier}-count`}
                    style={{
                        marginLeft: '0px',
                        whiteSpace: 'nowrap',
                        ...countStyles,
                    }}
                    {...{ [shouldMask ? 'data-clarity-mask' : 'data-clarity-unmask']: 'true' }}
                >
                    {count}
                </AppText>
            )}
        </Row>
    );
};

const CustomerCanceledMessage = ({ duration }: { duration: string }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const customerCanceledTooltipId = 'duration-column-customer-canceled-tooltip';
    const handleMouseEnter = () => {
        setShowTooltip(true);
        recordTooltipViewed(customerCanceledTooltipId);
    };

    const handleMouseLeave = () => {
        setShowTooltip(false);
    };

    return (
        <>
            <Tooltip
                message={
                    <AppText style={{ whiteSpace: 'nowrap' }} size="small">
                        Customer canceled after {duration} secs
                    </AppText>
                }
                showTooltip={showTooltip}
                distance={18}
                placement="top"
            >
                <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                    <AppText color="red50">Customer Canceled</AppText>
                </div>
            </Tooltip>
        </>
    );
};

function durationColumn(): TableColumn<CallLogRow> {
    return {
        key: 'duration',
        name: 'Duration',
        sortable: false,
        contentJustify: 'center',
        render: row => {
            if (row?.callStatus === 'STATUS_CANCELED') {
                return (
                    <Row flexGrow={1} justifyContent="center">
                        <CustomerCanceledMessage duration={row?.canceledCallRingTimeInSecs} />
                    </Row>
                );
            }
            if (row?.callStatus === 'STATUS_TIMED_OUT') {
                return (
                    <Row flexGrow={1} justifyContent="center">
                        <AppText color="red50">Timed Out</AppText>
                    </Row>
                );
            }
            if (row?.callStatus === 'STATUS_REJECTED') {
                return (
                    <Row flexGrow={1} justifyContent="center">
                        <AppText color="red50">Rejected</AppText>
                    </Row>
                );
            }
            if (!callHasEnded(row)) {
                return (
                    <Row flexGrow={1} justifyContent="center">
                        <AppText>Live</AppText>
                    </Row>
                );
            }
            if (!row?.startTime) {
                return <Column></Column>;
            }
            const duration = getFormattedCallDuration(row);
            return (
                <Row flexGrow={1} justifyContent="center" data-clarity-unmask="true">
                    <AppText>{duration}</AppText>
                </Row>
            );
        },
    };
}

function dateColumn(): TableColumn<CallLogRow> {
    return {
        key: 'date',
        name: 'Time of Call',
        sortable: false,
        contentJustify: 'flex-end',
        render: row => {
            if (!row?.startTime) {
                return <Column></Column>;
            }
            const [day, time] = formatDateColumn(row.startTime.toISOString(), {
                returnDayAndTime: true,
                dateFormat: { month: 'short', day: 'numeric', year: 'numeric' },
            });
            const Paragraph = AppText.withComponent('p');
            return (
                <Row flexGrow={1} justifyContent="flex-end" textAlign="right">
                    <Paragraph color={row.callStatus !== SUCCESSFUL_CALL_STATUS ? 'red50' : 'dark'}>
                        <time dateTime={row.startTime.toISOString()} data-clarity-unmask="true">
                            {time}
                            <br />
                            {day}
                        </time>
                    </Paragraph>
                </Row>
            );
        },
    };
}

function dropdownMenuColumn(props: CallLogColumnProps): TableColumn<CallLogRow> {
    function enableDropdown(
        callStatus: StreemApiCallLogEntryCallStatus | undefined,
        endTime: Date | undefined,
    ) {
        return (
            !['STATUS_REJECTED', 'STATUS_TIMED_OUT', 'STATUS_CANCELED'].includes(callStatus) &&
            endTime != null
        );
    }

    return {
        key: 'menu',
        name: '',
        sortable: false,
        contentJustify: 'center',
        render: (row: CallLogRow) => {
            return (
                <DropdownMenuColumn
                    row={row}
                    handleRoomArchive={props.handleRoomArchive}
                    enableDropdown={enableDropdown(row?.callStatus, row?.endTime)}
                />
            );
        },
    };
}

export const columnBuilder = {
    customerColumn,
    geoLocationColumn,
    teamMemberColumn,
    artifactColumn,
    durationColumn,
    dateColumn,
    dropdownMenuColumn,
};
